import {
  Component,
  OnInit,
  signal,
  computed,
  inject,
  input,
  effect,
  untracked,
  ElementRef,
  OnDestroy,
  output,
} from '@angular/core';
import { SplitFlapCharacter } from '../split-flap-character/split-flap-character';
import { SplitFlapAudioService } from '../../services/split-flap-audio.service';
import { BoardConfig, BoardMessage, DEFAULT_ALPHABET } from '../../models/board.model';
import { accentInk, messageGrid, normalizeMessage } from '../../utils/message-layout';
import { prefersReducedMotion } from '../../utils/reduced-motion';

/**
 * The board's cycle: reveal the first message, wait for the flaps to settle,
 * hold the message for the rotation interval, then blank before the next one.
 */
type PhaseKind = 'reveal' | 'settle' | 'display' | 'blank';

/** Blank interstitial between messages, which forces a re-flip */
const BLANK_INTERSTITIAL_MS = 100;

/**
 * Reusable split-flap board display component
 * Handles message rotation, expired message filtering, and character grid generation
 */
@Component({
  selector: 'app-split-flap-board',
  standalone: true,
  imports: [SplitFlapCharacter],
  templateUrl: './split-flap-board.html',
  styleUrl: './split-flap-board.css',
  host: {
    '[class.compact]': 'compact()',
  },
})
export class SplitFlapBoard implements OnInit, OnDestroy {
  protected audioService = inject(SplitFlapAudioService);

  private readonly FLIP_STEP_MS = 300; // 250ms flip + 50ms pause per step

  // The board moves through one phase at a time, so a single timer covers all
  // of them. Tracking the phase and its deadline is what lets the board be
  // paused and resumed with the time it had left, rather than restarted.
  private phase: PhaseKind | null = null;
  private phaseEndsAt = 0;
  private phaseTimer: ReturnType<typeof setTimeout> | null = null;
  private pausedRemainingMs: number | null = null;

  // Track the previously displayed characters to compute flip distances
  private previousChars: string[] = [];

  // Required inputs
  displayMessage = output<string | null>();
  config = input.required<BoardConfig>();
  messages = input.required<BoardMessage[]>();

  // Optional inputs
  showHeader = input<boolean>(true);
  showFooter = input<boolean>(true);
  headerText = input<string>('');
  footerText = input<string>('');

  // Compact mode: sizes the grid to its container instead of the viewport,
  // for embedding the board inline (e.g. the landing page preview)
  compact = input<boolean>(false);

  // Silence flip audio for this board only, ignoring the global mute setting
  muted = input<boolean>(false);

  // Characters on the drum, passed down to every flap. Trimming unused
  // characters shortens every flip; anything outside the drum shows as blank.
  alphabet = input<string>(DEFAULT_ALPHABET);

  // Beat of blank flaps before the first message. A full second gives the flip
  // sound time to load, so a board arriving on a cold cache is not silent for
  // its opening message. Shorten it only where the blank panel would read as a
  // board that failed to load, such as a preview embedded above the fold.
  initialRevealMs = input<number>(1000);

  // Hold the board still while it cannot be seen - scrolled out of view or in
  // a background tab - and pick the same flip back up on return.
  //
  // Off by default. A full-page board is the point of the page it is on and
  // has to keep running whether or not anyone is watching it, so this is only
  // for a board embedded alongside other content, like the landing preview.
  // A per-board setting for user boards would flow into this same input.
  pauseWhenNotVisible = input<boolean>(false);

  // Flag to track if we've shown the initial message
  private readonly isInitialized = signal(false);

  // Current message index
  private readonly currentMessageIndex = signal(0);

  // Force re-flip by toggling to blank state before showing next message
  private readonly showBlank = signal(false);

  private readonly now = signal(Date.now());
  private expiryTimer?: ReturnType<typeof setInterval>;

  // Filter out expired messages
  private readonly activeMessages = computed(
    () => {
      const now = new Date(this.now());
      return this.messages().filter((msg) => {
        if (!msg.expiresAt) return true;
        return new Date(msg.expiresAt) > now;
      });
    },
    { equal: (a, b) => a.length === b.length && a.every((message, index) => message === b[index]) },
  );

  // Current message (computed from index)
  protected readonly currentMessage = computed(() => {
    // Start with all blanks until initialized
    if (!this.isInitialized()) {
      return '';
    }

    // Show blank state during transitions (forces re-flip for single messages)
    if (this.showBlank()) {
      return '';
    }

    const activeMsgs = this.activeMessages();
    if (activeMsgs.length === 0) {
      return ''; // No active messages
    }

    // Get current message text
    const msgIndex = this.currentMessageIndex() % activeMsgs.length;
    return activeMsgs[msgIndex].text;
  });

  protected readonly displayedMessage = computed(() => {
    if (!this.isInitialized() || this.showBlank()) return undefined;
    const messages = this.activeMessages();
    return messages[this.currentMessageIndex() % messages.length];
  });

  // Use the same line layout as the editor; animation still traverses every flap.
  protected readonly characters = computed(() => {
    const cfg = this.config();
    const message = this.displayedMessage();
    const normalized = message ? normalizeMessage(message, cfg.columns) : undefined;
    return messageGrid(normalized, cfg.rows, cfg.columns).map((char, index) => {
      const accent = index < cfg.columns && char !== ' ' ? normalized?.accentColor : null;
      return {
        char,
        staggerDelay: Math.random() * 200,
        background: accent || '#14140f',
        color: accent ? accentInk(accent) : '#f5f2e8',
      };
    });
  });

  // Expose config values for template
  protected readonly rows = computed(() => this.config().rows);
  protected readonly cols = computed(() => this.config().columns);

  // Both halves of "not visible" count: scrolled out of view, and tab in the
  // background. Only ever set when pauseWhenNotVisible is on.
  private readonly offScreen = signal(false);
  private readonly tabHidden = signal(false);
  protected readonly paused = computed(
    () => this.pauseWhenNotVisible() && (this.offScreen() || this.tabHidden()),
  );

  private readonly host = inject(ElementRef<HTMLElement>).nativeElement;
  private visibilityObserver: IntersectionObserver | null = null;
  private onTabVisibilityChange: (() => void) | null = null;

  constructor() {
    // Stop the clock on the way out, restart it with the time it had left
    effect(() => {
      const isPaused = this.paused();
      untracked(() => (isPaused ? this.pauseSchedule() : this.resumeSchedule()));
    });

    // A config or message change rebuilds the grid and restarts every flap, so
    // any rotation timer armed against the previous grid is now sized wrong and
    // would rotate the board mid-flip. Re-arm the schedule against the new one.
    effect(() => {
      const cfg = this.config();
      this.activeMessages();

      // Turning reduced motion on changes what the schedule should wait for,
      // so re-arm rather than leaving the animated timings in place
      prefersReducedMotion();

      untracked(() => {
        // ngOnInit owns the first schedule, after the initial reveal
        if (!this.isInitialized()) return;

        // The blank interstitial re-schedules itself once it clears; re-arming
        // here would cancel its timer and strand the board on blank flaps
        if (this.showBlank()) return;

        // A different cell count remounts every flap (see the grid's track
        // expression), so they all start from the blank again
        if (this.previousChars.length !== cfg.rows * cfg.columns) {
          this.previousChars = [];
        }

        this.scheduleNextRotation();
      });
    });
  }

  ngOnInit(): void {
    this.expiryTimer = setInterval(() => this.now.set(Date.now()), 1000);
    // Inputs are resolved by now, so a board that never pauses never has to
    // watch for visibility at all
    if (this.pauseWhenNotVisible()) this.watchVisibility();

    // Initial reveal: start with blanks, then flip to the first message
    this.armPhase('reveal', this.initialRevealMs());
  }

  private watchVisibility(): void {
    this.tabHidden.set(document.hidden);

    this.visibilityObserver = new IntersectionObserver((entries) =>
      this.offScreen.set(!entries[entries.length - 1].isIntersecting),
    );
    this.visibilityObserver.observe(this.host);

    this.onTabVisibilityChange = () => this.tabHidden.set(document.hidden);
    document.addEventListener('visibilitychange', this.onTabVisibilityChange);
  }

  /**
   * Start a phase and remember when it is due, so a pause can bank the time it
   * had left. Replaces whatever phase was running.
   */
  private armPhase(kind: PhaseKind, ms: number): void {
    this.clearPhase();
    this.phase = kind;
    this.phaseEndsAt = performance.now() + ms;

    if (this.paused()) {
      // Off screen: hold the phase at its full duration until we are back
      this.pausedRemainingMs = ms;
      return;
    }

    this.phaseTimer = setTimeout(() => {
      this.phaseTimer = null;
      this.phase = null;
      this.runPhase(kind);
    }, ms);
  }

  private runPhase(kind: PhaseKind): void {
    switch (kind) {
      case 'reveal':
        this.isInitialized.set(true);
        this.scheduleNextRotation();
        break;
      case 'settle':
        this.displayMessage.emit(this.displayedMessage()?.id || null);
        // Flaps have landed; hold the message for the rotation interval
        this.armPhase('display', (this.config().rotationInterval || 20) * 1000);
        break;
      case 'display':
        this.rotateToNextMessage();
        break;
      case 'blank': {
        const activeMsgs = this.activeMessages();
        if (activeMsgs.length === 0) return;
        this.currentMessageIndex.update((i) => (i + 1) % activeMsgs.length);
        this.showBlank.set(false);
        this.scheduleNextRotation();
        break;
      }
    }
  }

  private clearPhase(): void {
    if (this.phaseTimer) clearTimeout(this.phaseTimer);
    this.phaseTimer = null;
    this.phase = null;
    this.pausedRemainingMs = null;
  }

  /**
   * Bank the current phase's remaining time and stop the clock. The flaps hold
   * their position too (see the `paused` input on SplitFlapCharacter), so a
   * board scrolled away mid-flip picks the same flip back up on return.
   */
  private pauseSchedule(): void {
    if (this.pausedRemainingMs !== null || !this.phase) return;
    this.pausedRemainingMs = Math.max(0, this.phaseEndsAt - performance.now());
    if (this.phaseTimer) clearTimeout(this.phaseTimer);
    this.phaseTimer = null;
  }

  private resumeSchedule(): void {
    if (this.pausedRemainingMs === null || !this.phase) return;
    const kind = this.phase;
    const remaining = this.pausedRemainingMs;
    this.pausedRemainingMs = null;
    this.armPhase(kind, remaining);
  }

  /**
   * The character a flap will actually display. SplitFlapCharacter renders
   * anything off the drum as a blank, so settle times have to be measured
   * against the blank, not the character the message asked for.
   */
  private onDrum(char: string): string {
    return this.alphabet().includes(char) ? char : ' ';
  }

  /**
   * Compute the number of forward steps to go from one character to another
   * on the split-flap drum (always rotates forward, wrapping around).
   * Both arguments are expected to have been through onDrum().
   */
  private flipSteps(from: string, to: string): number {
    const alphabet = this.alphabet();
    if (from === to) return 0;

    const toIdx = alphabet.indexOf(to);
    if (toIdx === -1) return 0;

    // A flap seated on a character this drum does not have reseats at the
    // blank before cycling, so measure the rotation from there
    const fromIdx = Math.max(0, alphabet.indexOf(from));

    let steps = toIdx - fromIdx;
    if (steps < 0) steps += alphabet.length;
    return steps;
  }

  /**
   * Calculate settle time based on the actual flip distances between
   * the previous and current character grids.
   */
  private calculateSettleTimeMs(): number {
    const currentChars = this.characters();
    let maxFlipTime = 0;

    for (let i = 0; i < currentChars.length; i++) {
      const from = this.previousChars[i] ?? ' ';
      const to = this.onDrum(currentChars[i].char);
      const steps = this.flipSteps(from, to);
      const flipTime = currentChars[i].staggerDelay + steps * this.FLIP_STEP_MS;
      maxFlipTime = Math.max(maxFlipTime, flipTime);
    }

    // Store what the flaps actually landed on, for the next comparison
    this.previousChars = currentChars.map((c) => this.onDrum(c.char));

    return maxFlipTime;
  }

  /**
   * Schedule the next message rotation
   * Waits for current message to fully display, then triggers next rotation
   */
  private scheduleNextRotation(): void {
    this.displayMessage.emit(null);
    this.clearPhase();
    const activeMsgs = this.activeMessages();
    if (activeMsgs.length === 0) return;

    // Always recompute, so previousChars stays current either way. Under
    // reduced motion the flaps land in a single frame, so waiting out the
    // animated settle would hold every message for seconds longer than asked.
    const settleTimeMs = this.calculateSettleTimeMs();
    const waitForSettleMs = prefersReducedMotion() ? 0 : settleTimeMs;

    this.armPhase('settle', waitForSettleMs);
  }

  ngOnDestroy(): void {
    clearInterval(this.expiryTimer);
    this.clearPhase();

    this.visibilityObserver?.disconnect();
    this.visibilityObserver = null;

    if (this.onTabVisibilityChange) {
      document.removeEventListener('visibilitychange', this.onTabVisibilityChange);
      this.onTabVisibilityChange = null;
    }
  }

  /**
   * Rotate to the next message (or re-show current for single message)
   */
  private rotateToNextMessage(): void {
    const activeMsgs = this.activeMessages();
    if (activeMsgs.length === 0) return;

    // Reduced motion: characters settle instantly, so the blank interstitial
    // would just read as a flicker. Swap straight to the next message.
    if (prefersReducedMotion()) {
      this.currentMessageIndex.update((i) => (i + 1) % activeMsgs.length);
      this.scheduleNextRotation();
      return;
    }

    // Flip to blank first, which forces a re-flip even for a single message.
    // The index advances when the blank phase ends.
    this.displayMessage.emit(null);
    this.showBlank.set(true);
    this.armPhase('blank', BLANK_INTERSTITIAL_MS);
  }
}
